/**
 * Formatting utilities for the Clinic Studios frontend
 *
 * Provides consistent formatting for durations, dates, and other display values.
 */

/**
 * Format duration in minutes to human-readable string
 *
 * @param minutes - Duration in minutes
 * @returns Formatted string like "30 min", "1 hr", "1 hr 30 min", "2 hrs"
 *
 * @example
 * formatDuration(30)  // "30 min"
 * formatDuration(60)  // "1 hr"
 * formatDuration(90)  // "1 hr 30 min"
 * formatDuration(120) // "2 hrs"
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const hourLabel = hours === 1 ? 'hr' : 'hrs';

  if (remainingMinutes === 0) {
    return `${hours} ${hourLabel}`;
  }

  return `${hours} ${hourLabel} ${remainingMinutes} min`;
};

/**
 * Get duration in minutes from booking object
 *
 * @param booking - Booking object with duration_minutes
 * @returns Duration in minutes
 *
 * @example
 * getDurationMinutes({ duration_minutes: 90 })  // 90
 * getDurationMinutes({})                        // 60 (default)
 */
export const getDurationMinutes = (booking: {
  duration_minutes?: number;
}): number => {
  return booking.duration_minutes ?? 60; // Default to 60 if undefined
};

/**
 * Format a 24-hour time string (HH:MM) to AM/PM display
 *
 * @example
 * formatTimeAMPM('09:00')  // "9:00 AM"
 * formatTimeAMPM('13:30')  // "1:30 PM"
 */
export const formatTimeAMPM = (time24: string): string => {
  const [hourStr, minStr] = time24.split(':');
  const hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minStr} ${period}`;
};

/**
 * Generate time options at 15-minute intervals for dropdown selectors.
 * Optionally constrained by a start/end range (e.g., clinic global hours).
 *
 * @param rangeStart - Earliest selectable time in HH:MM format (default '06:00')
 * @param rangeEnd - Latest selectable time in HH:MM format (default '22:00')
 * @returns Array of { value: 'HH:MM', label: 'H:MM AM/PM' } objects
 *
 * @example
 * generateTimeOptions('09:00', '17:00')
 * // [{ value: '09:00', label: '9:00 AM' }, { value: '09:15', label: '9:15 AM' }, ...]
 */
export const generateTimeOptions = (
  rangeStart: string = '06:00',
  rangeEnd: string = '22:00',
  incrementMinutes: number = 15
): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  const [startH, startM] = rangeStart.split(':').map(Number);
  const [endH, endM] = rangeEnd.split(':').map(Number);
  const startTotal = startH * 60 + startM;
  const endTotal = endH * 60 + endM;

  for (let mins = startTotal; mins <= endTotal; mins += incrementMinutes) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    options.push({ value, label: formatTimeAMPM(value) });
  }

  return options;
};
